/*******************************************************************************
 * Copyright (c) 2024 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {Injectable, inject} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {exhaustMap, of, tap} from 'rxjs';
import {TokenActions} from '@shared/common-core/store/actions';
import {NgxPermissionsService, NgxRolesService, ValidationFn} from 'ngx-permissions';
import {getAuthorizedScopes, PspPermission} from '@shared/models/psp-permission';
import {AUTH_STORAGE_KEYS} from '@shared/modules/auth/auth-keys';
import {LocalStorageService} from '@common-services/local-storage/local-storage.service';

/**
 * This file contains the effects for Token actions being dispatched
 */
@Injectable()
export class TokenEffects {
  private actions$ = inject(Actions);
  private rolesService = inject(NgxRolesService);
  private permissionsService = inject(NgxPermissionsService);
  private localStorageService = inject(LocalStorageService);

  /**
   * This effect is used to handle the setToken action.
   * It will first delete all existing roles for the current session.
   * It will then take the token that's being set, and using the token's roles,
   * it will also pre-populate the same role names which we can then use in the other code
   */
  setToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TokenActions.setToken),
      exhaustMap(({token}) => {
        this.rolesService.flushRolesAndPermissions();
        this.permissionsService.flushPermissions();
        if (token?.roles) {
          this.rolesService.addRoles(
            token.roles.reduce(
              (allRoles, currentRole: string) => {
                allRoles[currentRole] = () => true;
                return allRoles;
              },
              {} as {[roleName: string]: Array<string> | ValidationFn}
            )
          );

          this.permissionsService.addPermission(
            Object.values(PspPermission).reduce((allPermissions, currentPermission) => {
              const authorizedScopes = getAuthorizedScopes(currentPermission.split('.'));
              if (authorizedScopes.some(scope => token.scopes.includes(scope))) {
                allPermissions.push(currentPermission);
              }
              // Disable es-lint here as it's not returning an Action at all, and it's a false positive

              return allPermissions;
            }, [] as Array<string>)
          );
        }

        return of(TokenActions.removePartialToken());
      })
    );
  });

  /**
   * This effect is used to handle the removePartialToken action.
   * It will delete the partial token from the localStorage
   */
  removePartialToken$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TokenActions.removePartialToken),
        tap(() => {
          this.localStorageService.removeItem(AUTH_STORAGE_KEYS.partialToken);
        })
      );
    },
    {dispatch: false}
  );
}
